import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Toast from '../Toast';
import GymnastRoutineCard from './GymnastRoutineCard';
import GymnastRoutineEditor from './GymnastRoutineEditor';
import CopyRoutineDialog from './CopyRoutineDialog';
import SendBackDialog from './SendBackDialog';
import { APPARATUS } from './routineConstants';
import { discardWaiting, onRecordsSent } from '../../utils/offline';
import './GymnastRoutines.css';

const errorMessage = (err, fallback) => err.response?.data?.error || fallback;
const OFFLINE_SAVED = "Saved on this phone. It'll be sent when there's signal.";

// Routines a gymnast is working on. Coaches/admins (canEdit) can add, edit,
// customise, reorder, delete and approve; the gymnast (canSubmit) can add
// routines of their own for a coach to check; everyone else sees a read-only
// list, and nothing at all when the gymnast has no routines.
// With apparatusTabs (apparatus values the club uses), routines are split into
// sub-tabs, one per apparatus; pass apparatus/onApparatusChange to share the
// chosen tab with the page.
const GymnastRoutines = ({
  gymnastId, canEdit, canSubmit = false, collapsible = true, emptyText,
  apparatusTabs, apparatus: chosenApparatus, onApparatusChange,
}) => {
  const canAdd = canEdit || canSubmit;
  const [routines, setRoutines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(null); // null | 'new' | routine
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [copying, setCopying] = useState(null);
  const [sendingBack, setSendingBack] = useState(null);
  const [showRetired, setShowRetired] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [ownApparatus, setOwnApparatus] = useState('TRA');
  const [toast, setToast] = useState(null);
  const dismissToast = useCallback(() => setToast(null), []);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`/api/gymnast-routines/gymnast/${gymnastId}`);
      setRoutines(res.data);
      setLoadError(null);
    } catch (err) {
      setLoadError(errorMessage(err, 'Failed to load routines'));
    } finally {
      setLoading(false);
    }
  }, [gymnastId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => onRecordsSent(kind => { if (kind === 'gymnastRoutine') load(); }), [load]);

  const replaceRoutine = (updated) =>
    setRoutines(prev => prev.map(r => (r.id === updated.id ? updated : r)));

  // Rejects on failure so the editor keeps what the coach typed.
  const handleSave = async (payload, display) => {
    try {
      if (editing === 'new') {
        const offlinePreview = {
          gymnastId, status: 'WORKING_ON', notes: null, levelRoutine: null, levelRoutineId: null,
          submittedByGymnast: !canEdit, pendingVerification: !canEdit, ...payload, ...display,
        };
        const res = await axios.post(`/api/gymnast-routines/gymnast/${gymnastId}`, payload, { offlinePreview });
        setRoutines(prev => [...prev, res.data]);
        if (res.data.pending) setToast({ message: OFFLINE_SAVED });
        else setToast({ message: canEdit ? 'Routine added' : 'Routine added. Your coach will check it.' });
      } else {
        // A gymnast's change goes back to the coaches, even one that was sent back
        const recheck = canEdit ? {} : { pendingVerification: true, sentBackAt: null };
        const offlinePreview = { ...editing, ...payload, ...display, ...recheck, pending: undefined, failed: undefined };
        const res = await axios.put(`/api/gymnast-routines/${editing.id}`, payload, { offlinePreview });
        replaceRoutine(res.data);
        setToast({ message: res.data.pending ? OFFLINE_SAVED : 'Routine saved' });
      }
      setEditing(null);
    } catch (err) {
      setToast({ message: errorMessage(err, 'Failed to save routine'), type: 'error' });
      throw err;
    }
  };

  const handleCustomise = async (routine) => {
    try {
      const res = await axios.post(`/api/gymnast-routines/${routine.id}/customise`);
      replaceRoutine(res.data);
      setEditing(res.data);
    } catch (err) {
      setToast({ message: errorMessage(err, 'Failed to customise routine'), type: 'error' });
    }
  };

  const handleApprove = async (routine) => {
    try {
      replaceRoutine((await axios.post(`/api/gymnast-routines/${routine.id}/verify`)).data);
      setToast({ message: `${routine.name} approved` });
    } catch (err) {
      setToast({ message: errorMessage(err, 'Failed to approve routine'), type: 'error' });
    }
  };

  // Rejects on failure so the dialog stays open with the note
  const handleSendBack = async (note) => {
    const routine = sendingBack;
    try {
      replaceRoutine((await axios.post(`/api/gymnast-routines/${routine.id}/send-back`, { note })).data);
      setSendingBack(null);
      setToast({ message: `${routine.name} sent back` });
    } catch (err) {
      setToast({ message: errorMessage(err, 'Failed to send back routine'), type: 'error' });
      throw err;
    }
  };

  const handleDelete = async () => {
    const routine = confirmDelete;
    setConfirmDelete(null);
    try {
      await axios.delete(`/api/gymnast-routines/${routine.id}`);
      setRoutines(prev => prev.filter(r => r.id !== routine.id));
      setToast({ message: 'Routine deleted' });
    } catch (err) {
      setToast({ message: errorMessage(err, 'Failed to delete routine'), type: 'error' });
    }
  };

  // Swap a routine with its neighbour in the same visible group, then save the
  // whole order. Optimistic; reloads from the server if the save fails.
  const handleMove = async (group, index, delta) => {
    const a = group[index];
    const b = group[index + delta];
    if (!a || !b) return;
    const ids = routines.map(r => r.id);
    const ia = ids.indexOf(a.id);
    const ib = ids.indexOf(b.id);
    [ids[ia], ids[ib]] = [ids[ib], ids[ia]];
    setRoutines(ids.map(id => routines.find(r => r.id === id)));
    // Routines still waiting to send aren't on the server yet
    const onServer = new Set(routines.filter(r => !r.unsent).map(r => r.id));
    try {
      await axios.put(`/api/gymnast-routines/gymnast/${gymnastId}/reorder`, { routineIds: ids.filter(id => onServer.has(id)) });
    } catch (err) {
      setToast({ message: errorMessage(err, 'Failed to reorder routines'), type: 'error' });
      load();
    }
  };

  // Read-only viewers see nothing while empty, unless given text to show
  if (!canAdd && !loading && !loadError && routines.length === 0 && emptyText) return <p>{emptyText}</p>;
  if (!canAdd && (loading || loadError || routines.length === 0)) return null;

  const active = routines.filter(r => r.status !== 'RETIRED');
  const retired = routines.filter(r => r.status === 'RETIRED');

  // Apparatus sub-tabs: trampoline, plus any apparatus the club uses or the
  // gymnast has a routine on
  const tabs = apparatusTabs
    ? APPARATUS.filter(a => a.value === 'TRA' || apparatusTabs.includes(a.value) || routines.some(r => r.apparatus === a.value))
    : [];
  const wanted = chosenApparatus || ownApparatus;
  const current = tabs.some(a => a.value === wanted) ? wanted : 'TRA';
  const chooseApparatus = value => { setOwnApparatus(value); if (onApparatusChange) onApparatusChange(value); };
  const onTab = r => tabs.length <= 1 || r.apparatus === current;
  const shownActive = active.filter(onTab);
  const shownRetired = retired.filter(onTab);

  const renderGroup = (group) => group.map((routine, i) => (
    <GymnastRoutineCard
      key={routine.id}
      routine={routine}
      canEdit={canEdit}
      canSubmit={canSubmit}
      onApprove={() => handleApprove(routine)}
      onSendBack={canEdit ? () => setSendingBack(routine) : undefined}
      isFirst={i === 0}
      isLast={i === group.length - 1}
      onEdit={() => setEditing(routine)}
      onCustomise={() => handleCustomise(routine)}
      onCopy={() => setCopying(routine)}
      onDelete={() => setConfirmDelete(routine)}
      onMove={(delta) => handleMove(group, i, delta)}
      onDiscard={() => discardWaiting('gymnastRoutine', routine.id).then(load)}
    />
  ));

  return (
    <div className="card gymnast-routines">
      <div className="card-header gymnast-routines__header">
        <h4 className="card-title">
          {collapsible ? (
            <button
              type="button"
              className="gymnast-routines__toggle"
              aria-expanded={!collapsed}
              aria-controls={`routines-${gymnastId}`}
              onClick={() => setCollapsed(c => !c)}
            >
              <span aria-hidden="true">{collapsed ? '▸' : '▾'}</span> Routines
              {collapsed && routines.length > 0 && <span className="gymnast-routines__count"> ({routines.length})</span>}
            </button>
          ) : 'Routines'}
        </h4>
        {canAdd && (
          <button type="button" className="gymnast-routines__add" onClick={() => setEditing('new')} aria-label="Add routine">
            + Add
          </button>
        )}
      </div>

      {!(collapsible && collapsed) && (
        <div id={`routines-${gymnastId}`}>
          {loading && <p className="text-muted">Loading routines…</p>}
          {loadError && <p className="gymnast-routines__error">{loadError}</p>}
          {!loading && !loadError && routines.length === 0 && (
            <p className="text-muted">
              {canEdit ? 'No routines yet. Add the routines this gymnast is working on.' : (emptyText || 'No routines yet. Add the routines you are working on.')}
            </p>
          )}

          {tabs.length > 1 && (
            <div className="gymnast-tabs gymnast-tabs--apparatus" role="tablist" aria-label="Routine apparatus">
              {tabs.map(a => (
                <button
                  key={a.value}
                  type="button"
                  role="tab"
                  aria-selected={current === a.value}
                  className={`gymnast-tabs__tab${current === a.value ? ' active' : ''}`}
                  onClick={() => chooseApparatus(a.value)}
                >
                  {a.label}
                </button>
              ))}
            </div>
          )}
          {tabs.length > 1 && !loading && routines.length > 0 && shownActive.length === 0 && (
            <p className="text-muted">No {tabs.find(a => a.value === current).label.toLowerCase()} routines yet.</p>
          )}

          {APPARATUS.map(({ value, label }) => {
            const group = shownActive.filter(r => r.apparatus === value);
            if (group.length === 0) return null;
            return (
              <section key={value} className="gymnast-routines__group">
                {tabs.length <= 1 && <h5 className="gymnast-routines__group-title">{label}</h5>}
                {renderGroup(group)}
              </section>
            );
          })}

          {shownRetired.length > 0 && (
            <section className="gymnast-routines__group">
              <button
                type="button"
                className="gymnast-routines__retired-toggle"
                onClick={() => setShowRetired(s => !s)}
                aria-expanded={showRetired}
              >
                {showRetired ? '▾' : '▸'} Retired ({shownRetired.length})
              </button>
              {showRetired && renderGroup(shownRetired)}
            </section>
          )}
        </div>
      )}

      {editing && (
        <GymnastRoutineEditor
          routine={editing === 'new' ? null : editing}
          defaultApparatus={tabs.length > 1 ? current : undefined}
          gymnastId={gymnastId}
          byGymnast={!canEdit}
          onSave={handleSave}
          onCancel={() => setEditing(null)}
        />
      )}

      {copying && (
        <CopyRoutineDialog
          routine={copying}
          gymnastId={gymnastId}
          onCancel={() => setCopying(null)}
          onCopied={(n) => {
            setCopying(null);
            setToast({ message: `Copied to ${n} gymnast${n === 1 ? '' : 's'}` });
          }}
        />
      )}

      {sendingBack && (
        <SendBackDialog routine={sendingBack} onSend={handleSendBack} onCancel={() => setSendingBack(null)} />
      )}

      {confirmDelete && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h3>Delete routine?</h3>
              <button onClick={() => setConfirmDelete(null)} className="close-button">×</button>
            </div>
            <p>
              "{confirmDelete.name}" will be permanently deleted. To keep it as history,
              set its status to Retired instead.
            </p>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button type="button" className="btn btn-danger" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onDismiss={dismissToast} />}
    </div>
  );
};

export default GymnastRoutines;
